import { X } from "lucide-react";
import * as React from "react";
import { InlineIconButton } from "../atoms/inline-icon-button.js";
import { cx } from "../internal/cx.js";

/**
 * Active filter chips, usually rendered above a table or list. Each chip
 * represents one applied filter; clicking the close icon removes just
 * that filter. A trailing "Clear all" button removes them all at once.
 *
 * Dumb component: the parent owns the active filter set and the remove
 * / clear handlers.
 */

export interface FilterTag {
  id: string;
  /** Group label (e.g. "Status"). Used as the leading dim text. */
  group?: React.ReactNode;
  /** Active value (e.g. "Active, Pending"). */
  value: React.ReactNode;
}

export interface FilterTagsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  /** Active filter chips. Order is preserved. */
  tags: FilterTag[];
  /** Called when the X on a single tag is clicked. */
  onRemove?: (tagId: string) => void;
  /** Called when "Clear all" is clicked. Omit to hide that button. */
  onClearAll?: () => void;
  /** Label for the "Clear all" button. Default "Clear all". */
  clearAllLabel?: React.ReactNode;
  /** Optional leading label ("Filters:"). */
  leadingLabel?: React.ReactNode;
}

export const FilterTags = React.forwardRef<HTMLDivElement, FilterTagsProps>(function FilterTags(
  { className, clearAllLabel = "Clear all", leadingLabel, onClearAll, onRemove, tags, ...props },
  ref,
) {
  if (tags.length === 0) return null;
  return (
    <div ref={ref} className={cx("cw-v2-filter-tags", className)} {...props}>
      {leadingLabel ? <span className="cw-v2-filter-tags__leading">{leadingLabel}</span> : null}
      <ul className="cw-v2-filter-tags__list">
        {tags.map((tag) => (
          <li key={tag.id} className="cw-v2-filter-tags__item">
            <span className="cw-v2-filter-tags__pill">
              {tag.group ? <span className="cw-v2-filter-tags__group">{tag.group}:</span> : null}
              <span className="cw-v2-filter-tags__value">{tag.value}</span>
              {onRemove ? (
                <InlineIconButton
                  className="cw-v2-filter-tags__remove"
                  aria-label="Remove filter"
                  icon={<X aria-hidden />}
                  onClick={() => onRemove(tag.id)}
                  size="xs"
                />
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {onClearAll ? (
        <button type="button" className="cw-v2-filter-tags__clear-all" onClick={onClearAll}>
          {clearAllLabel}
        </button>
      ) : null}
    </div>
  );
});
FilterTags.displayName = "FilterTags";
